import { Calendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import deLocale from '@fullcalendar/core/locales/de';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';

const initVeranstaltungskalender = () => {
  const blocks = document.querySelectorAll('.veranstaltungskalender-block');
  
  blocks.forEach((block) => {
    // Prevent double initialization
    if (block.dataset.initialized === 'true') {
      return;
    }
    block.dataset.initialized = 'true';
    
    const defaultView = block.dataset.defaultView || 'calendar';
    const showFilters = block.dataset.showFilters === 'true';
    const eventsPerPage = parseInt(block.dataset.eventsPerPage) || 10;
    
    const container = document.createElement('div');
    container.className = 'veranstaltungskalender-container';
    
    if (showFilters) {
      const filterForm = createFilterForm();
      container.appendChild(filterForm);
      initChoices(filterForm);
    }
    
    const tabsContainer = createTabs(defaultView);
    container.appendChild(tabsContainer);
    
    const viewsContainer = createViewsContainer(defaultView);
    container.appendChild(viewsContainer);
    
    block.appendChild(container);
    
    const calendarEl = viewsContainer.querySelector('#calendar-view');
    let calendar = null;
    
    if (defaultView === 'calendar') {
      calendar = initCalendar(calendarEl);
    }
    
    setupTabSwitching(tabsContainer, viewsContainer, calendarEl, calendar);
    setupFilterSubmit(container, calendarEl, calendar, viewsContainer, eventsPerPage);
    
    loadInitialEvents(calendarEl, calendar, viewsContainer, eventsPerPage);
  });
};

const initChoices = (form) => {
  const selectElement = form.querySelector('#veranstaltungstyp');
  
  if (selectElement) {
    new Choices(selectElement, {
      removeItemButton: true,
      placeholder: true,
      placeholderValue: 'Veranstaltungstyp auswählen...',
      searchPlaceholderValue: 'Suchen...',
      noResultsText: 'Keine Ergebnisse gefunden',
      noChoicesText: 'Keine Optionen verfügbar',
      itemSelectText: '',
      loadingText: 'Laden...',
      removeItemButtonAlignLeft: false,
      removeItemIconText: () => '×',
      removeItemLabelText: (value, rawValue) => `${value} entfernen`,
      shouldSort: false,
      maxItemText: (maxItemCount) => {
        return `Nur ${maxItemCount} Werte können ausgewählt werden`;
      },
    });
  }
};

const createFilterForm = () => {
  const form = document.createElement('form');
  form.className = 'veranstaltungen-filter';
  form.innerHTML = `
    <div class="filter-row">
      <div class="filter-field">
        <label for="search-input">Suche</label>
        <input type="text" id="search-input" name="s" placeholder="Veranstaltung suchen..." />
      </div>
      
      <div class="filter-field">
        <label for="veranstaltungstyp">Veranstaltungstyp</label>
        <select id="veranstaltungstyp" name="veranstaltungstyp[]" multiple>
          <option value="unterricht">Unterricht</option>
          <option value="elternabend">Elternabend</option>
          <option value="ausflug">Ausflug</option>
          <option value="pruefung">Prüfung</option>
          <option value="ferien">Ferien</option>
          <option value="sonstiges">Sonstiges</option>
        </select>
      </div>
    </div>
    
    <div class="filter-row">
      <div class="filter-field filter-field-zielgruppe">
        <label>Zielgruppe</label>
        <div class="zielgruppe-checkboxes">
          <label class="zielgruppe-label zielgruppe-label-schueler">
            <input type="checkbox" name="zielgruppe[]" value="schueler" />
            <span class="badge badge-schueler">Schüler</span>
          </label>
          <label class="zielgruppe-label zielgruppe-label-eltern">
            <input type="checkbox" name="zielgruppe[]" value="eltern" />
            <span class="badge badge-eltern">Eltern</span>
          </label>
          <label class="zielgruppe-label zielgruppe-label-lehrer">
            <input type="checkbox" name="zielgruppe[]" value="lehrer" />
            <span class="badge badge-lehrer">Lehrer</span>
          </label>
        </div>
      </div>
      
      <div class="filter-field">
        <label for="date-from">Von</label>
        <input type="date" id="date-from" name="date_from" />
      </div>
      
      <div class="filter-field">
        <label for="date-to">Bis</label>
        <input type="date" id="date-to" name="date_to" />
      </div>
    </div>
    
    <div class="filter-actions">
      <button type="submit" class="filter-submit">Filter anwenden</button>
      <button type="reset" class="filter-reset">Zurücksetzen</button>
    </div>
  `;
  
  return form;
};

const createTabs = (defaultView) => {
  const tabs = document.createElement('div');
  tabs.className = 'veranstaltungen-tabs';
  tabs.innerHTML = `
    <button class="tab-btn ${defaultView === 'calendar' ? 'active' : ''}" data-view="calendar">
      <span class="dashicons dashicons-calendar-alt"></span> Kalender
    </button>
    <button class="tab-btn ${defaultView === 'list' ? 'active' : ''}" data-view="list">
      <span class="dashicons dashicons-list-view"></span> Liste
    </button>
  `;
  
  return tabs;
};

const createViewsContainer = (defaultView) => {
  const container = document.createElement('div');
  container.className = 'veranstaltungen-views';
  container.innerHTML = `
    <div id="calendar-view" class="view-content ${defaultView === 'calendar' ? 'active' : 'hidden'}"></div>
    <div id="list-view" class="view-content ${defaultView === 'list' ? 'active' : 'hidden'}">
      <div class="veranstaltungen-list"></div>
    </div>
  `;
  
  return container;
};

const initCalendar = (calendarEl) => {
  const calendar = new Calendar(calendarEl, {
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
    initialView: 'dayGridMonth',
    locale: deLocale,
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay'
    },
    buttonText: {
      today: 'Heute',
      month: 'Monat',
      week: 'Woche',
      day: 'Tag'
    },
    events: [],
    eventClick: (info) => {
      if (info.event.url) {
        info.jsEvent.preventDefault();
        window.location.href = info.event.url;
      }
    },
    height: 'auto',
  });
  
  calendar.render();
  return calendar;
};

const setupTabSwitching = (tabsContainer, viewsContainer, calendarEl, calendarInstance) => {
  const tabButtons = tabsContainer.querySelectorAll('.tab-btn');
  
  tabButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const targetView = btn.dataset.view;
      
      tabButtons.forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      
      const calendarView = viewsContainer.querySelector('#calendar-view');
      const listView = viewsContainer.querySelector('#list-view');
      
      if (targetView === 'calendar') {
        calendarView.classList.remove('hidden');
        calendarView.classList.add('active');
        listView.classList.remove('active');
        listView.classList.add('hidden');
        
        if (!calendarInstance) {
          calendarInstance = initCalendar(calendarEl);
          const events = window.currentVeranstaltungen || [];
          calendarInstance.removeAllEvents();
          calendarInstance.addEventSource(events);
        } else {
          setTimeout(() => {
            calendarInstance.updateSize();
          }, 100);
        }
      } else {
        listView.classList.remove('hidden');
        listView.classList.add('active');
        calendarView.classList.remove('active');
        calendarView.classList.add('hidden');
      }
    });
  });
};

const setupFilterSubmit = (container, calendarEl, calendarInstance, viewsContainer, eventsPerPage) => {
  const form = container.querySelector('.veranstaltungen-filter');
  if (!form) return;
  
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    
    const formData = new FormData(form);
    formData.append('action', 'nxt_veranstaltungen_search');
    formData.append('nonce', nxt_event_calendar.nonce);
    
    fetch(nxt_event_calendar.ajaxurl, {
      method: 'POST',
      body: formData
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        window.currentVeranstaltungen = data.data.calendar;
        
        if (calendarInstance) {
          calendarInstance.removeAllEvents();
          calendarInstance.addEventSource(data.data.calendar);
        }
        
        renderListView(data.data.list, viewsContainer, eventsPerPage);
      }
    })
    .catch(error => {
      console.error('Error fetching veranstaltungen:', error);
    });
  });
  
  form.addEventListener('reset', () => {
    setTimeout(() => {
      loadInitialEvents(calendarEl, calendarInstance, viewsContainer, eventsPerPage);
    }, 100);
  });
};

const loadInitialEvents = (calendarEl, calendarInstance, viewsContainer, eventsPerPage) => {
  const veranstaltungen = nxt_event_calendar.veranstaltungen || [];
  const calendarEvents = veranstaltungen.map(v => convertToCalendarFormat(v));
  
  window.currentVeranstaltungen = calendarEvents;
  
  if (calendarInstance) {
    calendarInstance.removeAllEvents();
    calendarInstance.addEventSource(calendarEvents);
  }
  
  renderListView(veranstaltungen, viewsContainer, eventsPerPage);
};

const convertToCalendarFormat = (veranstaltung) => {
  const zielgruppe_colors = {
    'schueler': '#3788d8',
    'eltern': '#4caf50',
    'lehrer': '#ff9800',
    'multiple': '#757575'
  };
  
  const zielgruppe = Array.isArray(veranstaltung.zielgruppe) ? veranstaltung.zielgruppe : [];
  let backgroundColor = zielgruppe_colors['multiple'];
  let className = 'zielgruppe-multiple';
  
  if (zielgruppe.length === 1) {
    backgroundColor = zielgruppe_colors[zielgruppe[0]] || zielgruppe_colors['multiple'];
    className = 'zielgruppe-' + zielgruppe[0];
  }
  
  const event = {
    id: veranstaltung.id,
    title: veranstaltung.title,
    url: veranstaltung.permalink,
    backgroundColor: backgroundColor,
    borderColor: backgroundColor,
    className: className
  };
  
  if (veranstaltung.startdatum) {
    let start = veranstaltung.startdatum;
    if (veranstaltung.startzeit && !veranstaltung.ganztaegig) {
      start += 'T' + veranstaltung.startzeit;
    } else {
      event.allDay = true;
    }
    event.start = start;
  }
  
  if (veranstaltung.enddatum) {
    let end = veranstaltung.enddatum;
    if (veranstaltung.endzeit && !veranstaltung.ganztaegig) {
      end += 'T' + veranstaltung.endzeit;
    }
    event.end = end;
  }
  
  event.extendedProps = {
    veranstaltungstyp: veranstaltung.veranstaltungstyp,
    ort: veranstaltung.ort,
    raum: veranstaltung.raum,
    zielgruppe: zielgruppe
  };
  
  return event;
};

const renderListView = (veranstaltungen, viewsContainer, eventsPerPage) => {
  const listContainer = viewsContainer.querySelector('.veranstaltungen-list');
  
  if (veranstaltungen.length === 0) {
    listContainer.innerHTML = '<p class="no-results">Keine Veranstaltungen gefunden.</p>';
    return;
  }
  
  let html = '';
  
  veranstaltungen.forEach((veranstaltung) => {
    const zielgruppe = Array.isArray(veranstaltung.zielgruppe) ? veranstaltung.zielgruppe : [];
    const zielgruppeLabels = {
      'schueler': 'Schüler',
      'eltern': 'Eltern',
      'lehrer': 'Lehrer'
    };
    
    const veranstaltungstypLabels = {
      'unterricht': 'Unterricht',
      'elternabend': 'Elternabend',
      'ausflug': 'Ausflug',
      'pruefung': 'Prüfung',
      'ferien': 'Ferien',
      'sonstiges': 'Sonstiges'
    };
    
    let zielgruppeBadges = '';
    zielgruppe.forEach(z => {
      if (zielgruppeLabels[z]) {
        zielgruppeBadges += `<span class="badge badge-${z}">${zielgruppeLabels[z]}</span>`;
      }
    });
    
    const dateStr = formatDateRange(
      veranstaltung.startdatum,
      veranstaltung.enddatum,
      veranstaltung.startzeit,
      veranstaltung.endzeit,
      veranstaltung.ganztaegig
    );
    
    const location = [veranstaltung.ort, veranstaltung.raum].filter(Boolean).join(', ');
    
    const hasMeta = dateStr || veranstaltung.veranstaltungstyp || location;
    
    html += `
      <article class="veranstaltung-item">
        <div class="veranstaltung-header">
          ${zielgruppeBadges ? `<div class="zielgruppe-badges">${zielgruppeBadges}</div>` : ''}
          <h3><a href="${veranstaltung.permalink}">${veranstaltung.title}</a></h3>
        </div>
        ${hasMeta ? `
          <div class="veranstaltung-meta">
            ${dateStr ? `
              <span class="meta-item meta-date">
                <span class="dashicons dashicons-calendar-alt"></span> ${dateStr}
              </span>
            ` : ''}
            ${veranstaltung.veranstaltungstyp ? `
              <span class="meta-item meta-type">
                <span class="dashicons dashicons-category"></span> ${veranstaltungstypLabels[veranstaltung.veranstaltungstyp] || veranstaltung.veranstaltungstyp}
              </span>
            ` : ''}
            ${location ? `
              <span class="meta-item meta-location">
                <span class="dashicons dashicons-location"></span> ${location}
              </span>
            ` : ''}
          </div>
        ` : ''}
        ${veranstaltung.content ? `<div class="veranstaltung-excerpt">${veranstaltung.content}</div>` : ''}
      </article>
    `;
  });
  
  listContainer.innerHTML = html;
};

const formatDateRange = (startDate, endDate, startTime, endTime, allDay) => {
  if (!startDate) return '';
  
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split('-');
    return `${day}.${month}.${year}`;
  };
  
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };
  
  let result = formatDate(startDate);
  
  if (startTime && !allDay) {
    result += ` ${formatTime(startTime)}`;
  }
  
  if (endDate && endDate !== startDate) {
    result += ` - ${formatDate(endDate)}`;
    if (endTime && !allDay) {
      result += ` ${formatTime(endTime)}`;
    }
  } else if (endTime && !allDay && endTime !== startTime) {
    result += ` - ${formatTime(endTime)}`;
  }
  
  return result;
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVeranstaltungskalender);
} else {
  initVeranstaltungskalender();
}
